import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';
import {
  AppDialogAlertOptions,
  AppDialogConfirmOptions,
  AppDialogPromptOptions,
  AppDialogType,
  registerAppDialog,
} from '../utils/appDialog';

type QueuedDialog = {
  mode: 'alert' | 'confirm' | 'prompt';
  message: string;
  options: AppDialogAlertOptions | AppDialogConfirmOptions | AppDialogPromptOptions;
  resolve: (value: boolean | void | string | null) => void;
};

const TYPE_STYLES: Record<
  AppDialogType,
  { color: string; bg: string; Icon: typeof Info }
> = {
  info: { color: '#2563eb', bg: '#dbeafe', Icon: Info },
  success: { color: '#16a34a', bg: '#dcfce7', Icon: CheckCircle2 },
  error: { color: '#dc2626', bg: '#fee2e2', Icon: AlertCircle },
  warning: { color: '#d97706', bg: '#fef3c7', Icon: AlertTriangle },
};

const DEFAULT_TITLES: Record<AppDialogType, string> = {
  info: 'Información',
  success: 'Listo',
  error: 'Error',
  warning: 'Atención',
};

interface AppDialogProviderProps {
  children: React.ReactNode;
}

const AppDialogProvider: React.FC<AppDialogProviderProps> = ({ children }) => {
  const queueRef = useRef<QueuedDialog[]>([]);
  const [current, setCurrent] = useState<QueuedDialog | null>(null);
  const [promptValue, setPromptValue] = useState('');

  const processQueue = useCallback(() => {
    setCurrent((active) => {
      if (active || queueRef.current.length === 0) {
        return active;
      }
      const next = queueRef.current.shift() ?? null;
      if (next?.mode === 'prompt') {
        const promptOptions = next.options as AppDialogPromptOptions;
        setPromptValue(promptOptions.defaultValue ?? '');
      }
      return next;
    });
  }, []);

  const enqueue = useCallback(
    (item: Omit<QueuedDialog, 'resolve'>) =>
      new Promise<boolean | void | string | null>((resolve) => {
        queueRef.current.push({ ...item, resolve });
        processQueue();
      }),
    [processQueue]
  );

  const closeCurrent = useCallback(
    (result?: boolean | string | null) => {
      setCurrent((active) => {
        if (active) {
          active.resolve(result);
        }
        return null;
      });
      setPromptValue('');
      window.setTimeout(processQueue, 0);
    },
    [processQueue]
  );

  useEffect(() => {
    registerAppDialog({
      alert: (message, options) =>
        enqueue({
          mode: 'alert',
          message,
          options: options ?? {},
        }) as Promise<void>,
      confirm: (message, options) =>
        enqueue({
          mode: 'confirm',
          message,
          options: options ?? {},
        }) as Promise<boolean>,
      prompt: (message, options) =>
        enqueue({
          mode: 'prompt',
          message,
          options: options ?? {},
        }) as Promise<string | null>,
    });

    return () => registerAppDialog(null);
  }, [enqueue]);

  const dialogType = (current?.options.type ?? 'info') as AppDialogType;
  const styles = TYPE_STYLES[dialogType];
  const Icon = styles.Icon;
  const title = current?.options.title ?? DEFAULT_TITLES[dialogType];
  const confirmText =
    current?.mode === 'confirm'
      ? current.options.confirmText ?? 'Confirmar'
      : current?.mode === 'prompt'
      ? current.options.confirmText ?? 'Aceptar'
      : current?.options.confirmText ?? 'Aceptar';
  const cancelText =
    (current?.options as AppDialogConfirmOptions | AppDialogPromptOptions | undefined)
      ?.cancelText ?? 'Cancelar';
  const destructive = Boolean(
    (current?.options as AppDialogConfirmOptions | undefined)?.destructive
  );
  const promptOptions =
    current?.mode === 'prompt'
      ? (current.options as AppDialogPromptOptions)
      : null;

  return (
    <>
      {children}
      <Dialog
        open={Boolean(current)}
        onClose={() => {
          if (current?.mode === 'alert') {
            closeCurrent();
          } else if (current?.mode === 'prompt') {
            closeCurrent(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)' },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{title}</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: '50%',
                backgroundColor: styles.bg,
                flexShrink: 0,
              }}
            >
              <Icon className="w-5 h-5" style={{ color: styles.color }} />
            </Box>
            <Box sx={{ flex: 1, pt: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', color: '#374151', mb: promptOptions ? 2 : 0 }}
              >
                {current?.message}
              </Typography>
              {promptOptions && (
                <TextField
                  fullWidth
                  autoFocus
                  label={promptOptions.inputLabel ?? promptOptions.placeholder ?? 'Valor'}
                  placeholder={promptOptions.placeholder}
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && promptValue.trim()) {
                      closeCurrent(promptValue.trim());
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          {(current?.mode === 'confirm' || current?.mode === 'prompt') && (
            <Button
              onClick={() => closeCurrent(current?.mode === 'prompt' ? null : false)}
              color="inherit"
            >
              {cancelText}
            </Button>
          )}
          <Button
            onClick={() => {
              if (current?.mode === 'confirm') {
                closeCurrent(true);
              } else if (current?.mode === 'prompt') {
                closeCurrent(promptValue.trim() || null);
              } else {
                closeCurrent();
              }
            }}
            variant="contained"
            color={destructive ? 'error' : 'primary'}
            autoFocus={current?.mode !== 'prompt'}
            disabled={current?.mode === 'prompt' && !promptValue.trim()}
          >
            {confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AppDialogProvider;
